/**
 * Shared primitives for the Lanternfall hub.
 *
 * Everything here is deliberately the *lowest* module in the hub's import graph
 * (`index.ts` → `App.tsx` → tabs → `common.tsx`) so no two hub files ever form a cycle;
 * Vite's HMR gets confused by cycles in ways that only show up after ten minutes of
 * editing, which is exactly when nobody wants to debug a module graph.
 */

import type { ComponentChildren, VNode } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import {
  ARMOR_SLOT_NAMES,
  CALLINGS,
  CRAFTING_ENABLED,
  HUB_TAB_IDS,
  ITEMS,
  ItemLocation,
  LEVEL_CAP,
  LOCATION_ARMOR_SLOT,
  MAX_DURABILITY,
  OVERFLOW_TABS,
  Rarity,
  RARITY_COLORS,
  RARITY_NAMES,
  SKILL_TREES,
  WEAPON_LOCATION,
  callingCanUse,
  dps,
  durabilityOf,
  equipLocationFor,
  isBroken,
  isDamageable,
  isEquipLocation,
  isOnboarded,
  itemValue,
  levelCanUse,
  repairCost,
  resolveModifiers,
  secondaryLabel,
  unlockedTabs,
  weaponForItem,
  xpForLevel,
  type HubTabId,
  type ItemDef,
  type ItemStack,
  type OnboardingProgress,
} from '@stillsun/shared';
import { apiTry, unwrapList } from '../../net/api.js';
import { progressStore, type ProgressState } from '../../net/progress-store.js';
import { session, type SessionState } from '../../net/session.js';
import { settings, type Settings } from '../settings.js';
import { Icon, ItemIcon, type IconName } from '../icons.js';

// --------------------------------------------------------------------- tabs

/**
 * The tab list lives in `@stillsun/shared` now, because `unlockedTabs` decides which of these
 * a given ledger may see and the gate and the shell must not be able to disagree about the
 * set. Re-exported under the old names so nothing downstream had to change.
 */
export const TAB_IDS = HUB_TAB_IDS;
export type TabId = HubTabId;

export const TAB_LABELS: Record<TabId, string> = {
  deploy: 'Deploy',
  calling: 'Calling',
  skills: 'Skills',
  stash: 'Stash & Loadout',
  ground: 'Ground',
  workbench: 'Workbench',
  vendor: 'Vendor',
  contracts: 'Contracts',
  marks: 'Marks',
  terminal: 'Terminal',
  roadmap: 'Roadmap',
  changelog: 'Changelog',
  choir: 'Choir',
  settings: 'Settings',
};

/** One drawing per tab, so the Town strip reads as a row of places rather than a row of words. */
export const TAB_ICONS: Record<TabId, IconName> = {
  deploy: 'deploy',
  calling: 'calling',
  skills: 'skills',
  stash: 'loadout',
  ground: 'ground',
  workbench: 'workbench',
  vendor: 'vendor',
  contracts: 'contracts',
  marks: 'marks',
  terminal: 'terminal',
  roadmap: 'roadmap',
  changelog: 'changelog',
  choir: 'choir',
  settings: 'settings',
};

// ------------------------------------------------------------ briefing rail

/**
 * The order of service. Four steps, sung in order, each one a tab that already existed —
 * the rail is a *view* over `TabId`, not a second navigation model, so `ctl.setTab` stays
 * the only way anything moves and `HubHandle.openTerminal()` keeps working untouched.
 */
export const STEPS = [
  { n: 1, numeral: 'I', tab: 'calling', title: 'Calling', icon: 'calling' },
  { n: 2, numeral: 'II', tab: 'stash', title: 'Loadout', icon: 'loadout' },
  { n: 3, numeral: 'III', tab: 'ground', title: 'Ground', icon: 'ground' },
  { n: 4, numeral: 'IV', tab: 'deploy', title: 'Deploy', icon: 'deploy' },
] as const satisfies readonly {
  n: number;
  numeral: string;
  tab: TabId;
  title: string;
  icon: IconName;
}[];

export type StepNumber = 1 | 2 | 3 | 4;

/**
 * Which step a tab belongs to. Tabs missing from this map are Town, not briefing, and
 * light no ribbon at all. `noUncheckedIndexedAccess` makes the miss explicit.
 */
export const STEP_OF: Partial<Record<TabId, StepNumber>> = {
  calling: 1,
  skills: 1,
  stash: 2,
  ground: 3,
  contracts: 3,
  deploy: 4,
};

/**
 * The Town strip: places in Lanternfall you can walk into. Contracts lives inside step 3, and
 * the reading room (Terminal, Roadmap, Changelog, Choir) has moved behind the strip's MORE
 * chevron — those four are never locked, but a first-time player should count places in town on
 * one hand, not read a row of eight words they have no use for yet.
 */
export const TOWN_IDS = [
  'workbench',
  'vendor',
  'marks',
  'settings',
] as const satisfies readonly TabId[];

/** Behind MORE, at the end of the strip. Always reachable, never in the strip itself. */
export const MORE_IDS: readonly TabId[] = OVERFLOW_TABS;

// -------------------------------------------------------------- what is open

/**
 * The onboarding ledger, assembled from the two documents the shell already reads: the shared
 * `/api/progress` cache and the stash. Everything in it is server-owned — the client is only
 * deciding what to *draw*, never what a player is allowed to do, and every route behind a hidden
 * tab is exactly as authoritative as it was before.
 */
export function onboardingProgress(
  progress: ProgressState,
  items: ItemStack[] | null,
  wick: number,
): OnboardingProgress {
  const data = progress.data;
  return {
    level: data?.level ?? 1,
    wick,
    skillPoints: data?.skillPoints ?? 0,
    counters: data?.counters ?? {},
    calling: data?.calling ?? 0,
    hasMaterials: (items ?? []).some((s) => ITEMS[s.itemId]?.kind === 'material'),
  };
}

/** One rung of the briefing rail. */
export type Step = (typeof STEPS)[number];

export interface HubGate {
  /** Every tab this ledger may see. */
  open: ReadonlySet<TabId>;
  /** The briefing rail, already filtered. */
  steps: Step[];
  /** The Town strip, already filtered. */
  town: TabId[];
  /** True once The Waking has been walked out of (or grandfathered). */
  onboarded: boolean;
}

/**
 * What the hub draws for this ledger.
 *
 * The rail hides step I in exactly one case: the player has a Calling, is still level 1 and has
 * no unspent point. That is the state a player is in for the ten minutes after The Waking — they
 * answered "who woke?" thirty seconds ago and re-asking it is the overwhelm the whole feature
 * exists to remove. It comes back the moment Skills does, at level 2, which is also the first
 * moment there is anything new to say inside it.
 */
export function hubGate(p: OnboardingProgress): HubGate {
  const open = new Set<TabId>(unlockedTabs(p));
  // Phase 2c: crafting is dark (`CRAFTING_ENABLED === false` in the shared contract) until the
  // recipe table is re-cut for the new item lines. Closed here, at the gate, rather than in
  // `App.tsx`'s nav — a tab that is not open is also not reachable by a stale deep link, and the
  // unlock-announcement pass reads the same set.
  if (!CRAFTING_ENABLED) open.delete('workbench');
  const settled =
    typeof p.calling === 'number' && p.calling > 0 && p.level < 2 && p.skillPoints < 1;
  return {
    open,
    steps: STEPS.filter((s) => open.has(s.tab) && !(s.tab === 'calling' && settled)),
    town: TOWN_IDS.filter((id) => open.has(id)),
    onboarded: isOnboarded(p),
  };
}

// ---------------------------------------------------------- unlock announcements

const SEEN_TABS_KEY = 'stillsun.hub.seen-tabs';

function readSeenTabs(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_TABS_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

/**
 * Which tabs opened since the last time this browser looked, marking them seen as it answers.
 *
 * Remembered per browser rather than per account because it is a *notification*, not progression:
 * the server already knows which tabs are open, and the only question here is whether this person
 * has had the news yet. First run is silent — a brand-new player is told nothing has unlocked,
 * because everything they can see is simply where they started.
 */
export function takeNewlyUnlocked(open: ReadonlySet<TabId>): TabId[] {
  const seen = readSeenTabs();
  const first = seen.size === 0;
  const fresh: TabId[] = [];
  for (const id of open) {
    if (seen.has(id)) continue;
    seen.add(id);
    if (!first) fresh.push(id);
  }
  if (fresh.length > 0 || first) {
    try {
      localStorage.setItem(SEEN_TABS_KEY, JSON.stringify([...seen]));
    } catch {
      /* private mode: a missed toast is not worth guarding */
    }
  }
  return fresh;
}

// ------------------------------------------------------------ deploy draft

export type DeployMode = 'raid' | 'expedition';

/**
 * The only mode a player can actually take out right now (Chris, 2026-09-04): Raid stays
 * on the board as a coming-soon chevron, but nothing may leave the hub as one. Everything
 * that can produce a mode — the draft, a remembered deploy, the Deploy button — passes
 * through here, so there is exactly one place that decides what is playable.
 */
export const PLAYABLE_MODE: DeployMode = 'expedition';

export function playableMode(mode: DeployMode): DeployMode {
  return mode === PLAYABLE_MODE ? mode : PLAYABLE_MODE;
}

/**
 * `?debug` (any value) unlocks the developer ground — the Lanternfall Yard test pad. The
 * flag is read once: the query string cannot change without a reload.
 */
export const DEBUG_GROUND: boolean =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debug');

export interface DeployDraft {
  mode: DeployMode;
  mapId: string;
}

/** What "same as last time" repeats: the exact request the host was handed last. */
export interface LastDeploy {
  mode: DeployMode;
  mapId: string;
  squad: string;
}

export interface DeployDraftHandle {
  draft: DeployDraft;
  set: (patch: Partial<DeployDraft>) => void;
  last: LastDeploy | null;
  /** Called by the Deploy button; persists the request so it can be repeated. */
  remember: (req: LastDeploy) => void;
}

const LAST_DEPLOY_KEY = 'stillsun.deploy.last';

function readLastDeploy(): LastDeploy | null {
  try {
    const raw = localStorage.getItem(LAST_DEPLOY_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return null;
    const rec = parsed as Record<string, unknown>;
    const mode = rec['mode'];
    const mapId = rec['mapId'];
    if (mode !== 'raid' && mode !== 'expedition') return null;
    if (typeof mapId !== 'string' || mapId.length === 0) return null;
    const squad = rec['squad'];
    // A raid remembered from before Raid was shelved must not come back through "Same as
    // last time" — coercing here fixes the sent request and the button's own label at once.
    return { mode: playableMode(mode), mapId, squad: typeof squad === 'string' ? squad : '' };
  } catch {
    return null;
  }
}

/**
 * The deploy request, held by the shell rather than by the Deploy tab, because step 3
 * writes it and step 4 reads it and the two are never mounted at the same time.
 */
export function useDeployDraft(initialMapId: string): DeployDraftHandle {
  const [last, setLast] = useState<LastDeploy | null>(readLastDeploy);
  const [draft, setDraft] = useState<DeployDraft>(() => ({
    mode: PLAYABLE_MODE,
    mapId: initialMapId,
  }));

  return {
    draft,
    set: (patch: Partial<DeployDraft>) => {
      setDraft((d) => ({ ...d, ...patch }));
    },
    last,
    remember: (req: LastDeploy) => {
      setLast(req);
      try {
        localStorage.setItem(LAST_DEPLOY_KEY, JSON.stringify(req));
      } catch {
        /* private mode: repeating a deploy is a convenience, not a feature to guard */
      }
    },
  };
}

// --------------------------------------------------------------- controller

export interface HubViewState {
  tab: TabId;
  visible: boolean;
  /** Bumped by `HubHandle.refresh()`; every loader keys off it. */
  nonce: number;
  /**
   * The hub is opening for the first time after The Waking. Exactly one screen in the game
   * introduces the town, and this is the flag that raises it.
   */
  firstLight: boolean;
}

interface EscapeEntry {
  fn: () => void;
}

/**
 * The imperative half of the hub. `mountHub` owns one of these and hands it to `App`;
 * the handle methods the host calls (show/hide/refresh/escape) all land here rather than
 * reaching into Preact, which has no synchronous "is a modal open?" answer.
 */
export class HubController {
  // The hub opens on Deploy.
  //
  // It used to open on Calling — step I, where a briefing starts (Chris, 2026-09-04). The Waking
  // took that job: anyone who reaches Lanternfall has already answered "who woke?" on a screen
  // built for the question, and everyone else was grandfathered in with a Calling on the record.
  // So the hub's job is now the one thing a returning Waker came here to do, which is leave.
  private state: HubViewState = { tab: 'deploy', visible: false, nonce: 0, firstLight: false };
  private readonly listeners = new Set<(s: HubViewState) => void>();
  /** Innermost-last. ESC unwinds this before the host sees the key. */
  private readonly escapes: EscapeEntry[] = [];

  get current(): HubViewState {
    return this.state;
  }

  subscribe(fn: (s: HubViewState) => void): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private emit(patch: Partial<HubViewState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of [...this.listeners]) {
      try {
        fn(this.state);
      } catch (err) {
        console.error('[stillsun] hub listener threw', err);
      }
    }
  }

  setTab(tab: TabId): void {
    if (this.state.tab !== tab) this.emit({ tab });
  }

  setVisible(visible: boolean): void {
    if (this.state.visible === visible) return;
    // Every visit to Lanternfall starts at Deploy, whatever tab was open when we left.
    this.emit(visible ? { visible, tab: 'deploy' } : { visible });
  }

  refresh(): void {
    this.emit({ nonce: this.state.nonce + 1 });
  }

  /** The player just walked out of The Waking: raise the card that introduces the town. */
  announceFirstLight(): void {
    this.emit({ firstLight: true });
  }

  clearFirstLight(): void {
    if (this.state.firstLight) this.emit({ firstLight: false });
  }

  /** Registers a dismissable layer. The returned disposer removes it from anywhere in the stack. */
  pushEscape(fn: () => void): () => void {
    const entry: EscapeEntry = { fn };
    this.escapes.push(entry);
    return () => {
      const i = this.escapes.indexOf(entry);
      if (i >= 0) this.escapes.splice(i, 1);
    };
  }

  /** True when a layer was dismissed. False lets the host handle ESC itself. */
  escape(): boolean {
    if (!this.state.visible) return false;
    const top = this.escapes[this.escapes.length - 1];
    if (!top) return false;
    top.fn();
    return true;
  }
}

// -------------------------------------------------------------------- hooks

export function useHubView(ctl: HubController): HubViewState {
  const [view, setView] = useState<HubViewState>(ctl.current);
  useEffect(() => ctl.subscribe(setView), [ctl]);
  return view;
}

export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>(session.current);
  useEffect(() => session.subscribe(setState), []);
  return state;
}

export function useSettings(): Settings {
  const [state, setState] = useState<Settings>(settings.current);
  useEffect(() => settings.subscribe(setState), []);
  return state;
}

/**
 * The shared `/api/progress` document. Every M3 tab reads it and the header draws the XP
 * bar from it, so it is fetched once per token rather than once per tab. `nonce` bumps on
 * `HubHandle.refresh()` (i.e. after a raid), which is exactly when it is stale.
 */
export function useProgress(token: string, nonce: number): ProgressState {
  const [state, setState] = useState<ProgressState>(progressStore.current);
  useEffect(() => progressStore.subscribe(setState), []);
  useEffect(() => {
    void progressStore.load(token, nonce > 0);
  }, [token, nonce]);
  return state;
}

/**
 * The player's Calling and the multiplier every fitted mod is scaled by (Tinker's passive and
 * any `modEffectMult` skill node). Both the Stash's rails and the Workbench's now draw the same
 * numbers, so the derivation lives here rather than being copied into two tabs — a rail that
 * disagreed with the gun the player actually shoots is worse than no rail.
 */
export function useEffectMult(
  token: string,
  nonce: number,
): { calling: number; effectMult: number } {
  const progress = useProgress(token, nonce);
  const calling = progress.data?.calling ?? 0;
  const effectMult =
    1 +
    resolveModifiers(
      CALLINGS[calling]?.passive,
      SKILL_TREES[calling],
      progress.data?.unlockedNodes ?? [],
    ).modEffectMult;
  return { calling, effectMult };
}

/** Fraction through the current level, 0..1. Level cap sits at a full bar, not a NaN. */
export function levelProgress(xp: number, level: number): number {
  if (level >= LEVEL_CAP) return 1;
  const base = xpForLevel(level);
  const next = xpForLevel(level + 1);
  const span = next - base;
  if (span <= 0) return 1;
  return Math.max(0, Math.min(1, (xp - base) / span));
}

/**
 * Registers `close` as the topmost ESC target while `active`. The callback is read
 * through a ref so a re-render with a fresh closure does not churn the stack order.
 */
export function useEscape(ctl: HubController, active: boolean, close: () => void): void {
  const ref = useRef(close);
  ref.current = close;
  useEffect(() => {
    if (!active) return;
    return ctl.pushEscape(() => {
      ref.current();
    });
  }, [ctl, active]);
}

export interface Loaded<T> {
  /** `null` means "the fetch finished and produced nothing" — an empty state, not a spinner. */
  data: T | null;
  loading: boolean;
  set: (value: T | null) => void;
  reload: () => void;
}

/**
 * One-shot fetch keyed by a string. `key` exists because loader closures are recreated
 * every render; re-running on identity would loop forever. Callers pass something like
 * `${nonce}:${token}` so a refresh or a sign-in re-fetches and nothing else does.
 */
export function useLoader<T>(load: () => Promise<T | null>, key: string): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadRef.current().then(
      (value) => {
        if (!alive) return;
        setData(value);
        setLoading(false);
      },
      (err: unknown) => {
        if (!alive) return;
        console.warn('[stillsun] hub loader failed', err);
        setData(null);
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
  }, [key, tick]);

  return {
    data,
    loading,
    set: setData,
    reload: () => {
      setTick((t) => t + 1);
    },
  };
}

/**
 * List reads keep the `null` that `apiTry` produces on a dead endpoint, because
 * `unwrapList` flattens "server is missing" and "you own nothing yet" into the same `[]`
 * and those two want very different copy.
 */
export async function loadList<T>(
  path: string,
  token: string,
  ...keys: string[]
): Promise<T[] | null> {
  // No token yet means the hub mounted before sign-in landed, which is every single boot:
  // firing the request anyway logged three "unauthorized" warnings on the callsign screen
  // before the player had typed anything. `null` is already "the ledger did not answer",
  // and the effect re-runs the moment a token arrives.
  if (token.length === 0) return null;
  const payload = await apiTry<unknown[] | Record<string, unknown>>(path, { token });
  if (payload === null) return null;
  return unwrapList<T>(payload, ...keys);
}

// --------------------------------------------------------------- presentation

export const LEDGER_SILENT =
  "The Compact's ledger is not answering — try again after the next deploy.";

/** The small Bebas caption that titles a block in the briefing. */
export function Cap(props: { icon?: IconName; children: ComponentChildren }): VNode {
  return (
    <span class="hub-cap">
      {props.icon === undefined ? null : <Icon name={props.icon} size={14} />}
      {props.children}
    </span>
  );
}

export function Notice(props: { children: ComponentChildren }): VNode {
  return <p class="hub-notice">{props.children}</p>;
}

export function Loading(): VNode {
  return <p class="hub-notice">Reading the ledger…</p>;
}

/** The three states every read-only tab has: loading, unreachable, empty. */
export function ListState(props: {
  loading: boolean;
  offline: boolean;
  empty: boolean;
  emptyText: string;
}): VNode | null {
  if (props.loading) return <Loading />;
  if (props.offline) return <Notice>{LEDGER_SILENT}</Notice>;
  if (props.empty) return <Notice>{props.emptyText}</Notice>;
  return null;
}

export function Section(props: {
  title: string;
  hint: string;
  icon?: IconName;
  children: ComponentChildren;
}): VNode {
  return (
    <section class="hub-section">
      <h2 class="hub-h2">
        {props.icon === undefined ? null : <Icon name={props.icon} size={18} />}
        {props.title}
        {props.hint.length > 0 ? <span class="hub-hint">{props.hint}</span> : null}
      </h2>
      {props.children}
    </section>
  );
}

// ------------------------------------------------------------------ helpers

/** `RARITY_COLORS` holds numbers for the renderer; the DOM wants `#rrggbb`. */
export function rarityHex(rarity: number): string {
  const raw = RARITY_COLORS[rarity];
  const value = typeof raw === 'number' ? raw : RARITY_COLORS[0];
  return `#${value.toString(16).padStart(6, '0')}`;
}

export function rarityName(rarity: number): string {
  return RARITY_NAMES[rarity] ?? 'Common';
}

/**
 * Rail/border colour for a rarity tier.
 *
 * M6 inverted this special case rather than renaming it. Moon (`#f2effa`) is the panel's own
 * bright text colour, so a moon rail vanishes — and under the new WoW ladder moon is *Common*
 * (index 0), not the top rung. Legendary is orange now and reads perfectly well as a rail, so
 * it takes its own colour like every other tier; Common is the one that gets the thin wine-2
 * line instead. The item name still reads moon via `rarityHex`, so the tier is unmistakable.
 */
export function rarityRailHex(rarity: number): string {
  return rarity === Rarity.Common ? 'var(--wine-2)' : rarityHex(rarity);
}

/**
 * True when `rarityRailHex` substituted a flat line for the tier's own colour (Common only).
 * Callers that draw a *second* pass over the rail — Stash's inset glow — must not translucent
 * a `var(...)` token, which would produce the literal string `var(--wine-2)33`.
 */
export function rarityRailIsFlat(rarity: number): boolean {
  return rarity === Rarity.Common;
}

export function wick(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

export function when(ts: number | undefined): string {
  if (typeof ts !== 'number' || !Number.isFinite(ts) || ts <= 0) return '';
  // Server timestamps have arrived as both seconds and milliseconds during M2; anything
  // before 2001 in ms is far more likely to be a second count.
  const ms = ts < 1e11 ? ts * 1000 : ts;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function errText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message.length > 0 ? err.message : fallback;
}

// ==================================================== the item tooltip and menu
//
// Phase 2c: one hover card and one right-click menu, used by the hub's Stash, the paperdoll and
// the in-world bag alike. They live here rather than in any one of those three because an item
// has to read the same everywhere — a player who learns what a tooltip says at home must not have
// to learn it again mid-raid — and because the rules a menu greys an entry with (level, Calling,
// durability, where the piece is sitting) are the server's rules, and there should be exactly one
// client-side reading of them.

/** Where a stack currently is, in the words the menu and the tooltip use. */
export function placeLabel(stack: ItemStack): string {
  if (stack.location === WEAPON_LOCATION) return 'In hand';
  const slot = LOCATION_ARMOR_SLOT[stack.location];
  if (slot !== undefined) return `Worn — ${ARMOR_SLOT_NAMES[slot].toLowerCase()}`;
  switch (stack.location) {
    case ItemLocation.Stash:
      return 'In the stash';
    case ItemLocation.Backpack:
      return 'In the backpack';
    case ItemLocation.Satchel:
      return 'In the satchel';
    case ItemLocation.Quick:
      return 'On the quick bar';
    default:
      return 'Carried';
  }
}

/** The slot line a piece of equipment shows under its name: "Chest armour", "Weapon". */
function slotLabel(def: ItemDef): string {
  if (def.kind === 'weapon') return 'Weapon';
  if (def.kind === 'armor' && def.armorSlot !== undefined) {
    return `${ARMOR_SLOT_NAMES[def.armorSlot]} armour`;
  }
  return def.kind.charAt(0).toUpperCase() + def.kind.slice(1);
}

const callingName = (calling: number): string => CALLINGS[calling]?.name ?? 'a Calling';

/** A signed percentage, for a secondary that is a multiplier rather than a flat number. */
const pct = (v: number): string =>
  `${v > 0 ? '+' : '−'}${String(Math.abs(Math.round(v * 1000) / 10))}%`;
const flat = (v: number): string => `${v > 0 ? '+' : '−'}${String(Math.abs(Math.round(v)))}`;

/** Every secondary an armour piece can carry, in the words `SLOT_SECONDARY` deals them out. */
const SECONDARY_TEXT: Record<string, (v: number) => string> = {
  maxHp: (v) => `${flat(v)} max HP`,
  maxStamina: (v) => `${flat(v)} max stamina`,
  damageMult: (v) => `${pct(v)} damage`,
  speedMult: (v) => `${pct(v)} move speed`,
  staggerResist: (v) => `${pct(v)} stagger resist`,
  cooldownMult: (v) => `${pct(v)} cooldowns`,
};

/** The stat lines for one item: armour + secondary, or damage / speed / dps. */
export function itemStatLines(def: ItemDef): string[] {
  const out: string[] = [];
  if (def.kind === 'weapon') {
    const w = weaponForItem(def.id);
    if (w) {
      out.push(`${String(w.damage[0])}–${String(w.damage[1])} damage`);
      out.push(`${(w.speedMs / 1000).toFixed(1)} s swing · ${String(w.range)} m reach`);
      out.push(`${dps(w).toFixed(1)} damage per second`);
    }
    return out;
  }
  if (def.kind === 'armor') {
    out.push(`+${String(def.armor ?? 0)} armour`);
    for (const [key, value] of Object.entries(def.mods ?? {})) {
      if (typeof value !== 'number') continue;
      out.push(SECONDARY_TEXT[key]?.(value) ?? `${flat(value)} ${key}`);
    }
    if (out.length === 1 && def.armorSlot !== undefined) {
      out.push(`No ${secondaryLabel(def.armorSlot)} — that is what the tier above buys`);
    }
  }
  return out;
}

export interface ItemCardProps {
  stack: ItemStack;
  /**
   * Character level, or **null when the ledger has not answered** — an offline guest session, or
   * a client that dropped straight into a raid. Null is not "level 1": defaulting it would print
   * "Requires level 9" in red on a piece the player is visibly wearing. The requirement is still
   * drawn (it is a fact about the item), it is just never marked unmet on a guess.
   */
  level: number | null;
  /** `CallingId`, 0 when none has been picked yet, null when unknown — see `level`. */
  calling: number | null;
}

/** The body of the hover card — split out so the tooltip and the menu's header agree. */
export function ItemCard(props: ItemCardProps): VNode {
  const { stack } = props;
  const def = ITEMS[stack.itemId];
  if (!def) {
    return (
      <>
        <b class="item-card-name">{stack.itemId}</b>
        <p class="item-card-desc">Unknown to the Compact — a newer item than this build.</p>
      </>
    );
  }
  const dur = durabilityOf(stack);
  const wears = isDamageable(def);
  const broken = isBroken(stack);
  const levelOk = props.level === null || levelCanUse(def, props.level);
  const callingOk = props.calling === null || callingCanUse(def, props.calling);
  const stats = itemStatLines(def);
  return (
    <>
      <div class="item-card-head">
        <ItemIcon def={def} size={40} {...(wears ? { durability: dur } : {})} />
        <div class="item-card-id">
          <b class="item-card-name" style={{ color: rarityHex(def.rarity) }}>
            {def.name}
          </b>
          <span class="item-card-meta">
            {rarityName(def.rarity)} · {slotLabel(def)}
          </span>
          <span class="item-card-meta">{placeLabel(stack)}</span>
        </div>
      </div>

      {def.levelReq !== undefined && def.levelReq > 1 ? (
        <span class={levelOk ? 'item-card-req' : 'item-card-req unmet'}>
          {`Requires level ${String(def.levelReq)}`}
        </span>
      ) : null}
      {def.calling !== undefined ? (
        <span class={callingOk ? 'item-card-req' : 'item-card-req unmet'}>
          {`${callingName(def.calling)} only`}
        </span>
      ) : null}

      {stats.length > 0 ? (
        <ul class={broken ? 'item-card-stats dead' : 'item-card-stats'}>
          {stats.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}

      {wears ? (
        <div class="item-card-dur">
          <span class="item-card-dur-n">{`Condition ${String(dur)}/${String(MAX_DURABILITY)}`}</span>
          <span class={`item-card-dur-bar${broken ? ' broken' : ''}`}>
            <i style={{ width: `${String(Math.round((dur / MAX_DURABILITY) * 100))}%` }} />
          </span>
          {broken ? (
            <span class="item-card-broken">Broken — gives no stats until it is repaired.</span>
          ) : null}
        </div>
      ) : null}

      <p class="item-card-desc">{def.desc}</p>
      <span class="item-card-value">
        {`Sells for ${wick(itemValue(stack))} Wick${stack.qty > 1 ? ` · ${String(stack.qty)} held` : ''}`}
      </span>
    </>
  );
}

/**
 * The hover card. Clamped inside the viewport on both axes and offset from the cursor, so it
 * never covers the square it is describing and never runs off the bottom of a short window.
 */
export function ItemTooltip(props: ItemCardProps & { x: number; y: number }): VNode {
  const width = 268;
  const height = 260;
  const left =
    props.x + 18 + width > window.innerWidth
      ? Math.max(8, props.x - 18 - width)
      : Math.max(8, props.x + 18);
  const top = Math.max(8, Math.min(props.y + 14, Math.max(8, window.innerHeight - height - 8)));
  return (
    <div
      class="item-card item-tip"
      role="tooltip"
      style={{ left: `${String(left)}px`, top: `${String(top)}px`, width: `${String(width)}px` }}
    >
      <ItemCard stack={props.stack} level={props.level} calling={props.calling} />
    </div>
  );
}

/** One row of the right-click menu. A `reason` greys it and explains itself in the same line. */
export interface MenuEntry {
  key: string;
  label: string;
  /** Set means disabled: what the player is owed instead of a missing row. */
  reason?: string;
  /** Set means the row arms itself first and acts on the second press. */
  confirm?: string;
  run: () => void;
}

export interface ItemMenuProps {
  stack: ItemStack;
  x: number;
  y: number;
  entries: readonly MenuEntry[];
  onClose: () => void;
}

/** `.item-menu`'s own `min-width`, and its header plus a row, from `hub.css`. */
const MENU_ESTIMATE_W = 186;
const MENU_HEAD_H = 28;
const MENU_ROW_H = 34;
const MENU_EDGE = 6;

const menuEstimateHeight = (rows: number): number => MENU_HEAD_H + rows * MENU_ROW_H;

/** Keep a box of `w` x `h` fully inside the window, preferring the cursor's own corner. */
function clampMenu(x: number, y: number, w: number, h: number): { left: number; top: number } {
  return {
    left: Math.max(MENU_EDGE, Math.min(x, window.innerWidth - w - MENU_EDGE)),
    top: Math.max(MENU_EDGE, Math.min(y, window.innerHeight - h - MENU_EDGE)),
  };
}

/**
 * The right-click menu. Opens at the cursor, clamps itself inside the window after it has
 * measured its own height, and closes on Escape, on a click anywhere else, on a scroll and on a
 * window resize — every way a player can mean "not that".
 */
export function ItemMenu(props: ItemMenuProps): VNode {
  const box = useRef<HTMLDivElement | null>(null);
  /**
   * Clamped twice: once from an estimate before the first paint, then again from the measured
   * box. The estimate is what keeps the brief's "never opens off-screen" true on frame one —
   * a layout effect runs before paint, but its `setPos` schedules a second render, and a menu
   * opened on the right-hand edge should not depend on that landing in the same frame.
   */
  const [pos, setPos] = useState(() =>
    clampMenu(props.x, props.y, MENU_ESTIMATE_W, menuEstimateHeight(props.entries.length)),
  );
  const [armed, setArmed] = useState('');
  const close = useRef(props.onClose);
  close.current = props.onClose;

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    setPos(clampMenu(props.x, props.y, el.offsetWidth, el.offsetHeight));
  }, [props.x, props.y, props.entries.length]);

  useEffect(() => {
    const dismiss = (ev: Event): void => {
      if (ev.target instanceof Node && box.current?.contains(ev.target) === true) return;
      close.current();
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key !== 'Escape') return;
      ev.preventDefault();
      ev.stopPropagation();
      close.current();
    };
    const onResize = (): void => {
      close.current();
    };
    // Capture, so the menu is dismissed before whatever is underneath decides the click was
    // meant for it — a click on another bag square should close this and select that, once.
    window.addEventListener('mousedown', dismiss, true);
    window.addEventListener('contextmenu', dismiss, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('wheel', dismiss, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('mousedown', dismiss, true);
      window.removeEventListener('contextmenu', dismiss, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('wheel', dismiss, true);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const def = ITEMS[props.stack.itemId];
  return (
    <div
      ref={box}
      class="item-menu"
      role="menu"
      aria-label={def?.name ?? props.stack.itemId}
      style={{ left: `${String(pos.left)}px`, top: `${String(pos.top)}px` }}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
    >
      <div class="item-menu-head" style={def ? { color: rarityHex(def.rarity) } : {}}>
        {def?.name ?? props.stack.itemId}
      </div>
      {props.entries.map((entry) => {
        const disabled = entry.reason !== undefined;
        const isArmed = armed === entry.key;
        return (
          <button
            key={entry.key}
            type="button"
            role="menuitem"
            class={`item-menu-row${disabled ? ' off' : ''}${isArmed ? ' armed' : ''}`}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              if (entry.confirm !== undefined && !isArmed) {
                setArmed(entry.key);
                return;
              }
              setArmed('');
              entry.run();
              close.current();
            }}
          >
            <span class="item-menu-label">
              {isArmed && entry.confirm !== undefined ? entry.confirm : entry.label}
            </span>
            {disabled ? <span class="item-menu-why">{entry.reason}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The Equip / Unequip / Drop rows every surface shares, in that order, with Repair appended by
 * the caller when a counter is open. Entries are greyed with a reason rather than hidden
 * wherever the reason tells the player something ("Vanguard only", "Requires level 6"); a row
 * that would only ever say "not applicable" is left out.
 */
export function baseItemEntries(opts: {
  stack: ItemStack;
  /** Null when the ledger has not answered — see `ItemCardProps.level`. */
  level: number | null;
  calling: number | null;
  onEquip: (stack: ItemStack) => void;
  onUnequip: (stack: ItemStack) => void;
  onDrop: (stack: ItemStack) => void;
}): MenuEntry[] {
  const { stack } = opts;
  const def = ITEMS[stack.itemId];
  const entries: MenuEntry[] = [];
  const worn = isEquipLocation(stack.location);
  const equippable = def !== undefined && equipLocationFor(def) !== null;

  if (equippable && !worn) {
    // With no ledger to check against, nothing is refused here — the server is the authority on
    // both rules and will answer honestly. Greying a legal piece on a missing read would be the
    // worse failure: the player would think they had lost the right to wear their own armour.
    const reason =
      opts.level !== null && !levelCanUse(def, opts.level)
        ? `Requires level ${String(def.levelReq ?? 1)}`
        : opts.calling !== null && !callingCanUse(def, opts.calling)
          ? `${callingName(def.calling ?? 0)} only`
          : undefined;
    entries.push({
      key: 'equip',
      label: def.kind === 'weapon' ? 'Take in hand' : 'Wear',
      ...(reason === undefined ? {} : { reason }),
      run: () => {
        opts.onEquip(stack);
      },
    });
  }
  if (worn) {
    entries.push({
      key: 'unequip',
      label: 'Take off',
      run: () => {
        opts.onUnequip(stack);
      },
    });
  }
  entries.push({
    key: 'drop',
    label: 'Drop',
    // Above Rare a drop is a real loss, so it names what it is about to destroy. Everything
    // below goes on the first press: a stack of scrap is not worth a second one.
    ...(def !== undefined && def.rarity >= Rarity.Rare ? { confirm: `Destroy ${def.name}?` } : {}),
    ...(worn ? { reason: 'Take it off first' } : {}),
    run: () => {
      opts.onDrop(stack);
    },
  });
  return entries;
}

/**
 * The Repair row. Only offered while a counter is open — repairs are a vendor service, not
 * something a Waker does in the field — and greyed with the shortfall rather than hidden when
 * the purse is short, so the player learns the price either way.
 */
export function repairEntry(opts: {
  stack: ItemStack;
  purse: number;
  onRepair: (stack: ItemStack, cost: number) => void;
}): MenuEntry | null {
  const cost = repairCost(opts.stack);
  if (cost <= 0) return null;
  const short = opts.purse < cost;
  return {
    key: 'repair',
    label: `Repair · ${wick(cost)} Wick`,
    ...(short ? { reason: `${wick(cost - opts.purse)} Wick short` } : {}),
    run: () => {
      opts.onRepair(opts.stack, cost);
    },
  };
}
